import config from '../common/config';
import { Facility } from '@reliance/types/models/Facility';
import { IApiResponse, IRelianceApi, IStatusResponse } from '../common/reliance-api-service';
import { EnabledDisabled } from '../models/enums';
import { ConditionComparator, EventNotificationActionType, IEventNotificationAction, IEventNotificationCondition, IEventNotificationTrigger, newPREATrigger, newPREATriggerAction, newTextMessageAlertTrigger } from './event-notification-factory';
import { AddAlertData } from './TextMessageAlertAddModal';
import api from '../common/api';

export interface IEmailAction {
  actionId?: number;
  name: string;
  address: string;
}

export interface IPREAConfig {
  triggerId?: number;
  templateId?: number;
  emailAction: IEmailAction;
}

export interface IEventNotificationService {
  getTriggers(facilityId: number): Promise<IApiResponse<IEventNotificationTrigger[]>>;
  getEmailActions(trigger: IEventNotificationTrigger): Array<IEventNotificationAction>;
  createTrigger(trigger: IEventNotificationTrigger): Promise<IApiResponse<IStatusResponse>>;
  deleteAction(triggerId: number, actionId: number): Promise<IApiResponse<IStatusResponse>>;
  deleteTrigger(triggerId: number): Promise<IApiResponse<IStatusResponse>>;

  getPREATrigger(facilityId: number): Promise<IEventNotificationTrigger>
  createPREATrigger(triggerConfig: IPREAConfig, facility?: Facility): Promise<IApiResponse<IStatusResponse>>;
  createPREAAction(triggerConfig: IPREAConfig): Promise<IApiResponse<IStatusResponse>>;
  updatePREAAction(actionConfig: IPREAConfig): Promise<IApiResponse<IStatusResponse>>;
  createPREATriggerAndOrAction(config: IPREAConfig, facility?: Facility): Promise<IApiResponse<IStatusResponse>>;

  getTextMessageAlertTriggers(facilityId: number): Promise<IEventNotificationTrigger[]>
  createTextMessageAlertTrigger(triggerConfig: AddAlertData, facility: Facility): Promise<IApiResponse<IStatusResponse>>;
}

export function hasPREACondition(conditions: IEventNotificationCondition[]): boolean
{
  return conditions.reduce((hasCondition, condition) => (
    hasCondition || (
      condition.metadataPath == 'variable_call_type' &&
      condition.comparator == ConditionComparator.EQ &&
      condition.value == 'prea-report' &&
      condition.status == 'Enabled'
    )
  ), false);
}

export function getPREATrigger(triggers: IEventNotificationTrigger[])
{
  return triggers.filter((trigger) =>
    trigger.eventType == 'cdr.processed' &&
    hasPREACondition(trigger.conditions)
  ).pop();
}

export function hasTextMessageAlertCondition(conditions: IEventNotificationCondition[]): boolean
{
  return conditions.reduce((hasCondition, condition) => (
    hasCondition || condition.status == 'Enabled'
  ), false);
}

export function getTextMessageAlertTriggers(triggers: IEventNotificationTrigger[])
{
  return triggers.filter((trigger) =>
    trigger.eventType == 'reliancetelephone.connect.text-message' &&
    hasTextMessageAlertCondition(trigger.conditions)
  );
}

export class EventNotificationService implements IEventNotificationService {
  constructor() {
  }

  async getTriggers(facilityId: number): Promise<IApiResponse<IEventNotificationTrigger[]>>
  {
    return api.get('/event-notification/trigger', { params: { facilityId, status: 'Enabled', page: -1 } });
  }

  async getPREATrigger(facilityId: number): Promise<IEventNotificationTrigger>
  {
    const response = await this.getTriggers(facilityId);
    return getPREATrigger(response.data);
  }

  async getTextMessageAlertTriggers(facilityId: number): Promise<IEventNotificationTrigger[]>
  {
    const response = await this.getTriggers(facilityId);
    return getTextMessageAlertTriggers(response.data);
  }

  getEmailActions(trigger: IEventNotificationTrigger): Array<IEventNotificationAction>
  {
    return trigger ? trigger.actions.filter((action) => {
      return action.actionType == EventNotificationActionType.Email;
    }) : [];
  }

  protected hasPREACondition(conditions: Array<IEventNotificationCondition>): boolean
  {
    for (let item of conditions) {
      if (
        item.metadataPath == 'variable_call_type' &&
        item.comparator == '=' &&
        item.value == 'prea-report' &&
        item.status == 'Enabled'
      ) {
        return true;
      }
    }
    return false;
  }

  protected hasEmailAction(actions: Array<IEventNotificationAction>) : boolean
  {
    for (let item of actions) {
      if (item.actionType == EventNotificationActionType.Email)
        return true;
    }
    return false;
  }

  async createTrigger(trigger: IEventNotificationTrigger): Promise<IApiResponse<IStatusResponse>>
  {
    return api.post('/event-notification/trigger', {trigger: trigger});
  }

  async createPREATriggerAndOrAction(config: IPREAConfig, facility?: Facility): Promise<IApiResponse<IStatusResponse>>
  {
    return config.triggerId ?
      this.createPREAAction(config) : this.createPREATrigger(config, facility);
  }

  async createPREATrigger(triggerConfig: IPREAConfig, facility?: Facility): Promise<IApiResponse<IStatusResponse>>
  {
    const trigger = newPREATrigger();

    if (facility) {
      trigger.facilityId = facility.id;
      trigger.name = facility.name ? `${facility.name} (${facility.city}, ${facility.state}) PREA Report` : 'PREA Report';
    }

    const action = newPREATriggerAction();

    action.destination = triggerConfig.emailAction.address;
    action.destinationName = triggerConfig.emailAction.name;
    action.newActionTemplateName = config.eventNotification.templates['prea_report']['name'];
    action.newActionTemplateContent = config.eventNotification.templates['prea_report']['template'];

    trigger.actions[0] = action;

    return api.post('/event-notification/trigger', {
      trigger: trigger
    });
  }

  async createTextMessageAlertTrigger(data: AddAlertData, facility: Facility): Promise<IApiResponse<IStatusResponse>>
  {
    const trigger = newTextMessageAlertTrigger();

    if (facility) {
      trigger.facilityId = facility.id;
      trigger.name = facility.name ? `${facility.name} (${facility.city}, ${facility.state}) Text Alert` : 'Text Alert';
    }

    // Always require the persist operation to not send out alerts on updates
    trigger.conditions.push({
      metadataPath: '_operation',
      value: 'persist',
      comparator: ConditionComparator.EQ,
      status: EnabledDisabled.Enabled
    });

    // Add applicable conditions
    if (data.message) {
      trigger.conditions.push({
        metadataPath: 'message',
        value: data.message,
        comparator: ConditionComparator.CONTAINS,
        status: EnabledDisabled.Enabled
      });
    }
    if (data.inmateId) {
      trigger.conditions.push({
        metadataPath: '_inmateId',
        value: data.inmateId,
        comparator: ConditionComparator.EQ,
        status: EnabledDisabled.Enabled
      });
    }
    if (data.outsideParty) {
      trigger.conditions.push({
        metadataPath: 'customerAni',
        value: data.outsideParty.replace(/[^0-9]/g, ''),
        comparator: ConditionComparator.CONTAINS,
        status: EnabledDisabled.Enabled
      });
    }

    // Add all of the configured actions
    data.actions.map(actionData => {
      trigger.actions.push({
        actionType: actionData.type,
        destination: actionData.destination,
        destinationName: actionData.destinationName,
        actionTemplateId: config.eventNotification.textMessageAlertTemplateIds[actionData.type],
        includeRecording: false
      });
    });

    return api.post('/event-notification/trigger', { trigger });
  }

  async createPREAAction(actionConfig: IPREAConfig): Promise<IApiResponse<IStatusResponse>>
  {
    const action = newPREATriggerAction();

    action.actionTemplateId = actionConfig.templateId;
    action.destination = actionConfig.emailAction.address;
    action.destinationName = actionConfig.emailAction.name;

    return api.post(`/event-notification/${actionConfig.triggerId}/action`, {
      action: action
    });
  }

  async updatePREAAction(actionConfig: IPREAConfig): Promise<IApiResponse<IStatusResponse>>
  {
    const actionId = actionConfig.emailAction.actionId;
    if (actionId == null) {
      throw new Error('updatePREAAction requires emailAction.actionId');
    }

    const action = newPREATriggerAction() as IEventNotificationAction;
    action.id = actionId;
    action.destinationName = actionConfig.emailAction.name;
    action.destination = actionConfig.emailAction.address;

    return api.put(`/event-notification/${actionConfig.triggerId}/action/${actionId}`, {
      updates: action
    });
  }

  async deleteAction(triggerId: number, actionId: number): Promise<IApiResponse<IStatusResponse>>
  {
    return api.delete(`/event-notification/${triggerId}/action/${actionId}`);
  }

  async deleteTrigger(triggerId: number): Promise<IApiResponse<IStatusResponse>>
  {
    return api.put(`/event-notification/trigger/${triggerId}`, {
      updates: { status: EnabledDisabled.Disabled }
    });
  }
}

export default new EventNotificationService();
